import React, { useEffect, useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import '../style/Cohete.css';
import { RELEASE_THRESHOLD } from '../hooks/useRocketScrollState';

// Qué tan cerca del header queda el cohete cuando "llega arriba".
// En vez de un valor fijo, se calcula como un % del alto de la pantalla,
// para que quede a la altura del nombre "Joaquín Leiva." en el hero
// (que está centrado verticalmente y por lo tanto también depende del
// alto de la pantalla). Ajusta el 0.28 si quieres que quede más arriba
// (número menor) o más abajo (número mayor).
const TOP_OFFSET_RATIO = 0.28;
// Nunca queda más arriba que esto, para no meterse debajo del navbar (80px)
const MIN_TOP_OFFSET = 90;
// Distancia (desde el borde inferior de la pantalla) a la que debe quedar
// el cohete cuando está "posado" sobre la plataforma de EstacionLanzamiento.js.
// Debe coincidir con la posición del cohete DIBUJADO dentro de esa misma
// plataforma (ver EstacionLanzamiento.js), para que el traspaso entre uno y
// otro sea invisible.
const DOCKED_BOTTOM_OFFSET = 340;

// Este cohete es el que efectivamente VUELA. Está oculto (opacity 0) casi
// todo el tiempo: mientras el usuario no haya llegado al final absoluto de
// la página, el cohete "real" que se ve es el que está dibujado quieto,
// posado en la plataforma, dentro de EstacionLanzamiento.js -así se ve
// siempre estacionado, sin importar qué tanto se scrollee por el resto del
// sitio-.
//
// Este componente (fixed) recién se activa cuando `launching` pasa a true,
// es decir, cuando el scroll llega al final de la página (RELEASE_THRESHOLD
// en useRocketScrollState.js). En ese instante aparece exactamente en el
// mismo lugar en el que estaba el cohete estático (por eso comparten la
// misma posición "docked"), lo reemplaza sin salto visual, y a partir de
// ahí queda LIBERADO: scrollear hacia arriba lo mueve de verdad, subiendo
// hasta la zona del header.
const Cohete = ({ rocketState }) => {
  const { scrollYProgress, launching } = rocketState;
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const topOffset = Math.max(MIN_TOP_OFFSET, viewportHeight * TOP_OFFSET_RATIO);

  const bottomPosition = Math.max(
    topOffset + 40,
    viewportHeight - DOCKED_BOTTOM_OFFSET
  );

  // Entre RELEASE_THRESHOLD y 1 (el tramo final del scroll) el cohete se
  // mantiene clavado en bottomPosition -exactamente donde estaba el cohete
  // estático que reemplaza-, sin moverse nada. Recién por debajo de
  // RELEASE_THRESHOLD (scrolleando hacia arriba desde el final) empieza a
  // interpolar de verdad hacia topOffset, junto al header.
  const top = useTransform(
    scrollYProgress,
    [0, RELEASE_THRESHOLD, 1],
    [topOffset, bottomPosition, bottomPosition]
  );

  return (
    <motion.div
      className="rocket-fixed"
      style={{ top }}
      initial={false}
      animate={{ opacity: launching ? 1 : 0 }}
      // Sin transición al aparecer (debe coincidir en el acto con el
      // cohete estático que reemplaza); con una salida suave al llegar
      // arriba y desactivarse.
      transition={{ duration: launching ? 0 : 0.4, ease: 'easeOut' }}
    >
      <svg viewBox="0 0 260 220" className="rocket-svg" role="img" aria-label="Cohete despegando hacia el header">
        <g id="cohete-grupo" className="ls-rocket">
          <path
            d="M100 10 C 86 35 80 65 80 90 L 120 90 C 120 65 114 35 100 10 Z"
            className="ls-rocket-body"
          />
          <rect x="80" y="90" width="40" height="110" className="ls-rocket-body" />
          <circle cx="100" cy="125" r="9" className="ls-window" />
          <line x1="80" y1="170" x2="120" y2="170" className="ls-rocket-line" />
          <line x1="80" y1="190" x2="120" y2="190" className="ls-rocket-line" />
          <path d="M80 178 L 60 210 L 80 210 Z" className="ls-rocket-body" />
          <path d="M120 178 L 140 210 L 120 210 Z" className="ls-rocket-body" />

          {/* Llama + estela de humo. Solo se ven de verdad mientras el
              motion.div padre está en opacity 1 (launching), pero las
              animaciones CSS corren siempre en loop; al estar ocultas no
              tienen costo visual ni de layout. */}
          <g className="rocket-exhaust">
            <path d="M90 210 L 100 236 L 110 210 Z" className="ls-flame" />
            <circle cx="88" cy="216" r="6" className="ls-smoke ls-smoke-1" style={{ '--smoke-x': '-10px' }} />
            <circle cx="100" cy="219" r="7" className="ls-smoke ls-smoke-2" style={{ '--smoke-x': '4px' }} />
            <circle cx="112" cy="216" r="6" className="ls-smoke ls-smoke-3" style={{ '--smoke-x': '12px' }} />
            <circle cx="100" cy="221" r="5" className="ls-smoke ls-smoke-4" style={{ '--smoke-x': '-6px' }} />
          </g>
        </g>
      </svg>
    </motion.div>
  );
};

export default Cohete;